/**
 * Recommendation form: "patient has no email" flow (custom element).
 * - Optional acknowledgement popover before enabling the option.
 * - Synthetic email cx+[digits]@eyepromise.com from the mobile phone field (read-only while enabled).
 *
 * Usage: <recommendation-form-no-email data-form-id="recommendationForm" hidden></recommendation-form-no-email>
 */

class RecommendationFormNoEmail extends HTMLElement {
  connectedCallback() {
    if (this._bindingsDone) return;

    const formId = this.dataset.formId || 'recommendationForm';
    this.form = document.getElementById(formId);
    if (!this.form) return;

    this.toggle = this.form.querySelector('#no-email-toggle');
    this.emailInput = this.form.querySelector('input[name="email"]');
    this.mobileInput = this.form.querySelector('input[name="mobilePhone"]');
    if (!this.toggle || !this.emailInput) return;

    this.warningPopover = document.getElementById('no-email-warning-popover');
    this.warningConfirm = document.querySelector('.js-no-email-warning-confirm');
    this.hasWarningModal = Boolean(this.warningPopover && this.warningConfirm);

    this._isApplyingModalConfirm = false;

    this._boundToggle = this.hasWarningModal
      ? this.handleToggleChange.bind(this)
      : this.applyStateFromCheckbox.bind(this);
    this._boundConfirm = this.handleWarningConfirm.bind(this);
    this._boundPhoneInput = this.handlePhoneInput.bind(this);
    this._boundFormReset = this.handleFormReset.bind(this);

    this.toggle.addEventListener('change', this._boundToggle);
    if (this.hasWarningModal) {
      this.warningConfirm.addEventListener('click', this._boundConfirm);
    }
    if (this.mobileInput) {
      this.mobileInput.addEventListener('input', this._boundPhoneInput);
    }
    this.form.addEventListener('reset', this._boundFormReset);

    this.applyStateFromCheckbox();
    this._bindingsDone = true;
  }

  disconnectedCallback() {
    this.teardown();
  }

  teardown() {
    if (!this._bindingsDone) return;

    if (this.toggle) {
      this.toggle.removeEventListener('change', this._boundToggle);
    }
    if (this.hasWarningModal && this.warningConfirm) {
      this.warningConfirm.removeEventListener('click', this._boundConfirm);
    }
    if (this.mobileInput) {
      this.mobileInput.removeEventListener('input', this._boundPhoneInput);
    }
    if (this.form) {
      this.form.removeEventListener('reset', this._boundFormReset);
    }

    this._bindingsDone = false;
  }

  /** Re-apply email field state from the checkbox (e.g. after programmatic form updates). */
  syncFromForm() {
    this.applyStateFromCheckbox();
  }

  handleFormReset() {
    // Defer until after the browser finishes the full reset algorithm and any other
    // reset listeners (so #no-email-toggle.checked reflects defaults, not pre-reset state).
    queueMicrotask(() => this.applyStateFromCheckbox());
  }

  static buildSyntheticEmail(phoneValue) {
    const digits = String(phoneValue || '').replace(/\D/g, '');
    return digits ? `cx+${digits}@eyepromise.com` : '';
  }

  writeSyntheticEmailFromPhone() {
    if (!this.toggle.checked) return;
    this.emailInput.value = RecommendationFormNoEmail.buildSyntheticEmail(
      this.mobileInput ? this.mobileInput.value : ''
    );
  }

  applyStateFromCheckbox() {
    if (!this.toggle || !this.emailInput) return;

    if (this.toggle.checked) {
      this.emailInput.readOnly = true;
      this.emailInput.removeAttribute('required');
      this.writeSyntheticEmailFromPhone();
    } else {
      this.emailInput.readOnly = false;
      this.emailInput.setAttribute('required', 'true');
      this.emailInput.value = '';
    }
  }

  showWarningPopover() {
    if (!this.warningPopover || typeof this.warningPopover.showPopover !== 'function') return;
    this.warningPopover.showPopover();
    queueMicrotask(() => {
      if (this.warningConfirm && typeof this.warningConfirm.focus === 'function') {
        this.warningConfirm.focus();
      }
    });
  }

  hideWarningPopover() {
    if (!this.warningPopover || typeof this.warningPopover.hidePopover !== 'function') return;
    this.warningPopover.hidePopover();
  }

  handleToggleChange() {
    if (this._isApplyingModalConfirm) return;

    if (this.toggle.checked) {
      this.toggle.checked = false;
      this.applyStateFromCheckbox();
      this.showWarningPopover();
      return;
    }
    this.applyStateFromCheckbox();
  }

  handleWarningConfirm() {
    this._isApplyingModalConfirm = true;
    this.toggle.checked = true;
    this.applyStateFromCheckbox();
    this._isApplyingModalConfirm = false;
    this.hideWarningPopover();
  }

  handlePhoneInput() {
    if (this.toggle.checked) this.writeSyntheticEmailFromPhone();
  }
}

if (!customElements.get('recommendation-form-no-email')) {
  customElements.define('recommendation-form-no-email', RecommendationFormNoEmail);
}
